import { useListActionWorkflowOptions } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';

/**
 * Resolve a stored `workflow_action` reference to a concrete definition id.
 *
 * A reference is stored as `{ workflowId, workflowDefinitionId }`, where a
 * null/absent `workflowDefinitionId` means "latest published" — the lineage's
 * pinned version, resolved fresh rather than frozen into config (see
 * `backend/src/workflows/resolveWorkflowActionTarget.ts`). Selecting an action
 * in the config panel produces exactly that shape, so anything keyed only on
 * `config.workflowDefinitionId` sees nothing for a freshly picked action.
 *
 * `listActions` returns one option per action lineage whose `id` *is* the
 * pinned definition id, which makes it the client-side equivalent of the
 * runtime's pinned lookup. The query only runs when a lineage actually needs
 * resolving, so non-action nodes cost nothing.
 */
export function useResolvedActionDefinitionId(
  config: Record<string, unknown> | undefined,
): string | undefined {
  const pinnedDefinitionId =
    typeof config?.workflowDefinitionId === 'string'
      ? config.workflowDefinitionId
      : undefined;
  const workflowId =
    typeof config?.workflowId === 'string' ? config.workflowId : undefined;

  const needsLineageLookup = !pinnedDefinitionId && !!workflowId;
  const { data } = useListActionWorkflowOptions({
    enabled: needsLineageLookup,
  });

  if (pinnedDefinitionId) return pinnedDefinitionId;
  if (!workflowId) return undefined;

  return data?.workflows.find((option) => option.workflowId === workflowId)?.id;
}

/**
 * The other direction: which action *lineage* a `workflow_action` reference
 * points at, for lookups that are version-agnostic by nature (who else uses
 * this action?).
 *
 * `config.workflowId` carries it directly in the shape the config panel writes.
 * A reference that stored only a version id falls back to the options list,
 * which knows each lineage's *pinned* definition — so a step pinned to an older
 * version resolves to nothing, and callers should treat that as "unknown"
 * rather than "unused".
 */
export function useResolvedActionLineageId(
  config: Record<string, unknown> | undefined,
): string | undefined {
  const workflowId =
    typeof config?.workflowId === 'string' ? config.workflowId : undefined;
  const definitionId =
    typeof config?.workflowDefinitionId === 'string'
      ? config.workflowDefinitionId
      : undefined;

  const { data } = useListActionWorkflowOptions({
    enabled: !workflowId && !!definitionId,
  });

  if (workflowId) return workflowId;
  if (!definitionId) return undefined;

  return data?.workflows.find((option) => option.id === definitionId)
    ?.workflowId;
}
